import { Maximize2, Minus, Plus, Scan, X } from "lucide-react";
import {
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { formatPercent } from "@/i18n/format";
import { cn } from "@/lib/utils";
import type { ScaleMode } from "./scale-mode.ts";

/** A drawing the Expand view can zoom and pan: an image URL and its size. */
export interface ZoomImage {
  src: string;
  alt: string;
  /**
   * The drawing's size in CSS px (Mermaid's viewBox), or only its proportions when `fill`; null
   * when unknown (the image's own proportions are used).
   */
  width: number | null;
  height: number | null;
  /**
   * The size is a ratio, not pixels (an SVG whose viewBox units are not CSS px): 100 % fills the
   * screen. Without it, 100 % is the drawing's own size.
   */
  fill?: boolean;
}

export interface WideBlockProps {
  /** Names the block: the scroll group, the Expand dialog's title and, by default, its button. */
  label: string;
  /** The Expand button's name when `Expand <label>` reads badly ("Expand table"). */
  expandLabel?: string;
  /** The block at its natural size: a table, a `<pre>`, an `<img>` with its own width. */
  children: ReactNode;
  /** The block's chrome, on the frame: border, radius, background ("rounded-lg border …"). */
  className?: string;
  /** Classes on the scrolling viewport inside the frame (padding). */
  contentClassName?: string;
  /**
   * The width it asks for before scrolling: `max-content` (default: a code line, a drawing) or
   * `min-content` (tables: each column at its narrowest, so prose tables keep the reading width
   * and only tables whose columns cannot fit widen).
   */
  fit?: "max-content" | "min-content";
  /** When Expand is offered: `auto` (default) once wider than its column; `always` (drawings). */
  expand?: "auto" | "always";
  /** A drawing: Expand shows it with zoom and pan instead of `children`. */
  zoom?: ZoomImage;
  /** A line under the block in the Expand view (a row-cap notice that must stay in sight). */
  note?: ReactNode;
  /**
   * A drawing shown inline either shrunk to the block's width (`fit`, never enlarged) or at its
   * own size, scrolling (`actual`). With `onScaleModeChange` and `naturalWidth`, the block offers
   * a Fit / Actual size toggle while the drawing is wider than the block; the children follow the
   * mode (in `fit` they cap their width to the block, so nothing scrolls). Expand keeps its own zoom.
   */
  scaleMode?: ScaleMode;
  onScaleModeChange?: (mode: ScaleMode) => void;
  /** The drawing's own width in CSS px, to tell whether Fit would shrink it. */
  naturalWidth?: number;
}

/**
 * A wide block (#379): a table, code, a diagram or a chart at its natural size. Inside a
 * `.wide-host` (a scroll area holding a reading column) it may widen past the column, centred on
 * it, up to `--wide-limit`; beyond that, and everywhere else, it scrolls sideways inside itself
 * (never the page), with a fade on the side that hides content. Overflowing, it is one tab stop
 * (arrow keys scroll it). Expand opens it full screen; a `zoom` drawing gets zoom and pan.
 * DESIGN.md "Wide blocks".
 */
export function WideBlock(props: WideBlockProps) {
  const { t } = useTranslation();
  const frame = useRef<HTMLDivElement>(null);
  const viewport = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const [edges, setEdges] = useState({
    start: false,
    end: false,
    overflow: false,
    wide: false,
    shrinks: false,
    small: false,
  });
  const [open, setOpen] = useState(false);
  // Breakout only straight under the host: in a nested scroll box (a card's preview) the block
  // could not be scrolled to on the side it overflows, so it stays in its column there.
  const [contained, setContained] = useState(false);

  const { naturalWidth, scaleMode, onScaleModeChange } = props;
  const measure = useCallback(() => {
    const el = viewport.current;
    const box = frame.current;
    if (!el || !box) {
      return;
    }
    const max = el.scrollWidth - el.clientWidth;
    const overflow = max > 1;
    const parent = box.parentElement?.clientWidth ?? box.offsetWidth;
    const next = {
      start: overflow && el.scrollLeft > 1,
      end: overflow && el.scrollLeft < max - 1,
      overflow,
      wide: overflow || box.offsetWidth > parent + 1,
      // The same in both modes: the frame asks for the drawing's width either way.
      shrinks: naturalWidth !== undefined && naturalWidth > contentWidth(el) + 1,
      // Fit would show it below the readable scale (the hint's threshold).
      small: naturalWidth !== undefined && contentWidth(el) < naturalWidth * TOO_SMALL,
    };
    setEdges((prev) =>
      prev.start === next.start &&
      prev.end === next.end &&
      prev.overflow === next.overflow &&
      prev.wide === next.wide &&
      prev.shrinks === next.shrinks &&
      prev.small === next.small
        ? prev
        : next,
    );
  }, [naturalWidth]);

  useLayoutEffect(() => {
    setContained(nestedScroller(frame.current));
    measure();
    const el = viewport.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    for (const child of el.children) {
      observer.observe(child);
    }
    return () => observer.disconnect();
  }, [measure]);
  // Streamed content can overflow without resizing anything observed (a code line growing in a
  // block already at its widest): measure again after every render; unchanged state is kept.
  useLayoutEffect(() => {
    measure();
  });

  const canExpand = props.expand === "always" || edges.wide;
  // In Actual size the toggle stays while it scrolls, so the way back to Fit is never lost.
  const canScale =
    scaleMode !== undefined &&
    onScaleModeChange !== undefined &&
    (edges.shrinks || (scaleMode === "actual" && edges.overflow));
  // Shrunk below the readable scale in Fit: say how to read it (Actual size or Expand).
  const tooSmall = canScale && scaleMode === "fit" && edges.small;
  const fade = edges.start || edges.end ? fadeMask(edges.start, edges.end) : undefined;

  return (
    <div
      ref={frame}
      data-wide-block=""
      className={cn(
        // Its content's width, at least the column's, at most the host's limit (else the
        // column's); centred on the column when wider (the parent is a flex column).
        "group/wide relative max-w-[var(--wide-limit,100%)] min-w-full self-center",
        props.fit === "min-content" ? "w-min" : "w-max",
        contained && "max-w-full",
        props.className,
      )}
    >
      <div
        ref={viewport}
        onScroll={measure}
        // Once it scrolls, a named tab stop: keyboard users scroll it with the arrow keys.
        {...(edges.overflow ? { tabIndex: 0, role: "group", "aria-label": props.label } : {})}
        className={cn(
          "overflow-x-auto overscroll-x-contain rounded-[inherit] outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
          props.contentClassName,
        )}
        style={fade}
      >
        {props.children}
      </div>
      {tooSmall ? (
        // In the frame, under the scroller (its fade mask would clip it), on a raised chip that
        // reads on the light diagram canvas in both themes.
        <div className="flex px-3 pb-2.5">
          <p className="rounded-md border border-line bg-surface-raised px-2 py-0.5 text-xs text-fg-secondary">
            {t("wideBlock.tooSmall")}
          </p>
        </div>
      ) : null}
      {canScale ? (
        <fieldset
          className={cn(
            "absolute top-1.5 flex h-7 items-center rounded-md border border-line bg-surface-raised p-0.5 shadow-xs",
            canExpand ? "right-10" : "right-1.5",
            REVEAL,
          )}
        >
          <legend className="sr-only">{t("wideBlock.size")}</legend>
          {(["fit", "actual"] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              aria-pressed={scaleMode === mode}
              onClick={() => onScaleModeChange(mode)}
              className={cn(
                "h-full rounded-[5px] px-2 text-xs font-medium text-fg-secondary transition-colors duration-(--duration-fast) outline-none hover:text-fg focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none",
                scaleMode === mode && "bg-surface-selected text-fg",
              )}
            >
              {t(mode === "fit" ? "wideBlock.fit" : "wideBlock.actualSize")}
            </button>
          ))}
        </fieldset>
      ) : null}
      {canExpand ? (
        <Button
          ref={button}
          type="button"
          variant="outline"
          size="icon-sm"
          aria-label={props.expandLabel ?? t("wideBlock.expandNamed", { name: props.label })}
          title={t("wideBlock.expand")}
          onClick={() => setOpen(true)}
          className={cn(
            "absolute top-1.5 right-1.5 size-7 bg-surface-raised text-fg-secondary shadow-xs",
            REVEAL,
          )}
        >
          <Maximize2 aria-hidden="true" strokeWidth={1.75} className="size-3.5" />
        </Button>
      ) : null}
      {/* Kept while open: a block that stops being wide (a rotation) must not close it. */}
      {canExpand || open ? (
        <ExpandDialog
          open={open}
          onClose={() => setOpen(false)}
          label={props.label}
          zoom={props.zoom}
          className={props.className}
          contentClassName={props.contentClassName}
          note={props.note}
          returnFocus={button}
        >
          {props.children}
        </ExpandDialog>
      ) : null}
    </div>
  );
}

/** Below this scale, Fit shows a drawing too small to read: the block says so. */
export const TOO_SMALL = 0.6;

/** The block's controls: revealed on hover or focus where hover exists; always shown on touch. */
const REVEAL =
  "opacity-0 transition-opacity duration-(--duration-fast) group-focus-within/wide:opacity-100 group-hover/wide:opacity-100 focus-visible:opacity-100 motion-reduce:transition-none [@media(hover:none)]:opacity-100";

/** The width inside the scroller's padding: what a drawing can take without scrolling. */
function contentWidth(el: HTMLElement): number {
  const style = getComputedStyle(el);
  const padding =
    (Number.parseFloat(style.paddingLeft) || 0) + (Number.parseFloat(style.paddingRight) || 0);
  return el.clientWidth - padding;
}

interface Size {
  width: number;
  height: number;
}

/** `size` scaled to fit inside `room`, keeping its proportions. */
function fitInto(size: Size, room: Size): Size {
  const k = Math.min(room.width / size.width, room.height / size.height);
  return { width: size.width * k, height: size.height * k };
}

/** Whether a scroll box other than the `.wide-host` sits between the block and its host. */
function nestedScroller(el: HTMLElement | null): boolean {
  for (let node = el?.parentElement; node; node = node.parentElement) {
    if (node.classList.contains("wide-host")) {
      return false;
    }
    const style = getComputedStyle(node);
    if (/(auto|scroll)/.test(`${style.overflowX} ${style.overflowY}`)) {
      return true;
    }
  }
  return false;
}

/** Fades the side(s) whose content is scrolled out of view; colour-independent (a mask). */
function fadeMask(start: boolean, end: boolean): CSSProperties {
  const image = `linear-gradient(to right, ${start ? "transparent" : "#000"} 0, #000 2rem, #000 calc(100% - 2rem), ${end ? "transparent" : "#000"} 100%)`;
  return { maskImage: image, WebkitMaskImage: image };
}

/** The zoom steps of the Expand view (fit sits between them). */
export const ZOOM_STEPS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4] as const;

/** The next zoom step above or below `scale`; past the last step (a fit under 25 %), `scale`. */
export function stepZoom(scale: number, direction: 1 | -1): number {
  const steps = direction > 0 ? ZOOM_STEPS : [...ZOOM_STEPS].reverse();
  const next = steps.find((s) => (direction > 0 ? s > scale + 0.001 : s < scale - 0.001));
  return next ?? scale;
}

function ExpandDialog(props: {
  open: boolean;
  onClose: () => void;
  label: string;
  zoom: ZoomImage | undefined;
  className: string | undefined;
  contentClassName: string | undefined;
  note: ReactNode;
  returnFocus: RefObject<HTMLButtonElement | null>;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={props.open} onOpenChange={(open) => (open ? undefined : props.onClose())}>
      <DialogContent
        showCloseButton={false}
        onCloseAutoFocus={(event) => {
          const target = props.returnFocus.current;
          if (target?.isConnected) {
            event.preventDefault();
            target.focus();
          }
        }}
        className={cn(
          // Full screen on phones; inset by 16px from sm.
          "top-0 left-0 flex h-dvh w-dvw max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none p-0 sm:top-4 sm:left-4 sm:h-[calc(100dvh-2rem)] sm:w-[calc(100dvw-2rem)] sm:max-w-none sm:rounded-xl",
        )}
      >
        {props.open && props.zoom ? (
          <ZoomView label={props.label} image={props.zoom} onClose={props.onClose} />
        ) : props.open ? (
          <>
            <ExpandHeader label={props.label} onClose={props.onClose} />
            {/* biome-ignore lint/a11y/useSemanticElements: a named scroll area, not a form fieldset */}
            <div
              // biome-ignore lint/a11y/noNoninteractiveTabindex: a scroll container must be focusable
              tabIndex={0}
              role="group"
              aria-label={props.label}
              className="min-h-0 flex-1 overflow-auto p-4 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset sm:p-6"
            >
              <div className={cn("w-max min-w-full", props.className)}>
                <div className={props.contentClassName}>{props.children}</div>
              </div>
              {props.note ? <div className="mt-2">{props.note}</div> : null}
            </div>
          </>
        ) : null}
        <DialogDescription className="sr-only">{t("wideBlock.description")}</DialogDescription>
      </DialogContent>
    </Dialog>
  );
}

function ExpandHeader(props: { label: string; onClose: () => void; children?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="flex h-bar shrink-0 items-center gap-2 border-b border-line pr-2 pl-4">
      <DialogTitle className="min-w-0 flex-1 truncate text-sm font-semibold">
        {props.label}
      </DialogTitle>
      {props.children}
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={t("wideBlock.close")}
        title={t("wideBlock.close")}
        onClick={props.onClose}
      >
        <X aria-hidden="true" strokeWidth={1.75} />
      </Button>
    </div>
  );
}

/**
 * A drawing full screen: fit to the screen when larger, else 100 %; zoom with the controls or
 * `+`, `-`, `0` (fit) and `1` (actual size); pan by dragging or with the arrow keys (the drawing
 * is a focusable scroll area). Zooming keeps the centre of the view in place. Nothing animates.
 */
function ZoomView(props: { label: string; image: ZoomImage; onClose: () => void }) {
  const { t } = useTranslation();
  const area = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState<Size | null>(null);
  const [room, setRoom] = useState<Size | null>(null);
  const given =
    props.image.width && props.image.height
      ? { width: props.image.width, height: props.image.height }
      : null;
  // 100 %: the drawing's own size in px, or (`fill`, or no size known) its proportions fitted to
  // the screen; with no proportions at all (an image without intrinsic size), the screen's.
  const natural =
    given && !props.image.fill ? given : room ? fitInto(given ?? measured ?? room, room) : null;
  const [scale, setScale] = useState<number | null>(null);
  // The scroll position to restore after a zoom, as a fraction of the content (the view's centre).
  const anchor = useRef<{ x: number; y: number } | null>(null);
  const drag = useRef<{ x: number; y: number; left: number; top: number; id: number } | null>(null);

  // The room to fit into, kept current as the window resizes or the phone rotates.
  useLayoutEffect(() => {
    const el = area.current;
    if (!el) {
      return;
    }
    const update = () =>
      setRoom((prev) => {
        const next = {
          width: Math.max(1, el.clientWidth - 48),
          height: Math.max(1, el.clientHeight - 48),
        };
        return prev && prev.width === next.width && prev.height === next.height ? prev : next;
      });
    update();
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const fit = useCallback(() => {
    if (!room || !natural) {
      return 1;
    }
    // No floor: a drawing many times the screen still fits whole (room is at least 1 px).
    return Math.min(1, room.width / natural.width, room.height / natural.height);
  }, [natural, room]);

  // The first scale once the area is measured and the drawing's size known: fit, never enlarged.
  useLayoutEffect(() => {
    if (scale === null && natural && room) {
      setScale(fit());
    }
  }, [scale, natural, room, fit]);

  // Remembers the drawing point at the view's centre, restored after the next scale change.
  const keepCentre = useCallback(() => {
    const el = area.current;
    if (el && el.scrollWidth > 0 && el.scrollHeight > 0) {
      anchor.current = {
        x: (el.scrollLeft + el.clientWidth / 2) / el.scrollWidth,
        y: (el.scrollTop + el.clientHeight / 2) / el.scrollHeight,
      };
    }
  }, []);
  const zoomTo = (next: number) => {
    keepCentre();
    setScale(next);
  };

  useLayoutEffect(() => {
    const el = area.current;
    const at = anchor.current;
    if (el && at && scale !== null) {
      el.scrollLeft = at.x * el.scrollWidth - el.clientWidth / 2;
      el.scrollTop = at.y * el.scrollHeight - el.clientHeight / 2;
      anchor.current = null;
    }
  }, [scale]);

  // A wheel with Ctrl or ⌘ (and a trackpad pinch, which browsers send as one) zooms.
  useEffect(() => {
    const el = area.current;
    if (!el) {
      return;
    }
    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        keepCentre();
        setScale((s) => {
          const current = s ?? 1;
          // Below 10 % (a fit), zooming out stays put rather than enlarging.
          const floor = Math.min(0.1, current);
          return Math.min(4, Math.max(floor, current * (event.deltaY < 0 ? 1.1 : 1 / 1.1)));
        });
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [keepCentre]);

  const current = scale ?? 1;
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.metaKey || event.ctrlKey || event.altKey) {
      return;
    }
    const keys: Record<string, () => number> = {
      "+": () => stepZoom(current, 1),
      "=": () => stepZoom(current, 1),
      "-": () => stepZoom(current, -1),
      "0": fit,
      "1": () => 1,
    };
    const next = keys[event.key];
    if (next) {
      event.preventDefault();
      zoomTo(next());
    }
  };
  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    const el = area.current;
    if (!el || event.button !== 0 || event.pointerType === "touch") {
      return;
    }
    drag.current = {
      x: event.clientX,
      y: event.clientY,
      left: el.scrollLeft,
      top: el.scrollTop,
      id: event.pointerId,
    };
    el.setPointerCapture?.(event.pointerId);
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const el = area.current;
    const d = drag.current;
    if (el && d && d.id === event.pointerId) {
      el.scrollLeft = d.left - (event.clientX - d.x);
      el.scrollTop = d.top - (event.clientY - d.y);
    }
  };
  const endDrag = () => {
    drag.current = null;
  };

  const size = natural
    ? { width: natural.width * current, height: natural.height * current }
    : undefined;
  return (
    <>
      <ExpandHeader label={props.label} onClose={props.onClose}>
        {/* biome-ignore lint/a11y/useSemanticElements: a group of zoom controls, not a fieldset */}
        <div className="flex items-center gap-0.5" role="group" aria-label={t("wideBlock.zoom")}>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t("wideBlock.zoomOut")}
            title={t("wideBlock.zoomOut")}
            disabled={current <= ZOOM_STEPS[0] + 0.001}
            onClick={() => zoomTo(stepZoom(current, -1))}
          >
            <Minus aria-hidden="true" strokeWidth={1.75} />
          </Button>
          <output
            aria-live="polite"
            aria-label={t("wideBlock.level")}
            className="w-12 text-center text-xs tabular-nums text-fg-secondary"
          >
            {formatPercent(current)}
          </output>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t("wideBlock.zoomIn")}
            title={t("wideBlock.zoomIn")}
            disabled={current >= 4 - 0.001}
            onClick={() => zoomTo(stepZoom(current, 1))}
          >
            <Plus aria-hidden="true" strokeWidth={1.75} />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-fg-secondary"
            onClick={() => zoomTo(fit())}
          >
            <Scan aria-hidden="true" strokeWidth={1.75} />
            <span className="sr-only sm:not-sr-only">{t("wideBlock.fit")}</span>
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="hidden text-fg-secondary sm:inline-flex"
            onClick={() => zoomTo(1)}
          >
            {t("wideBlock.actualSize")}
          </Button>
        </div>
      </ExpandHeader>
      {/* biome-ignore lint/a11y/useSemanticElements: a named pan area, not a form fieldset */}
      <div
        ref={area}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: the drawing pans with the arrow keys
        tabIndex={0}
        role="group"
        aria-label={t("wideBlock.panArea", { name: props.label })}
        aria-keyshortcuts="+ - 0 1"
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className="diagram-canvas min-h-0 flex-1 cursor-grab touch-pan-x touch-pan-y overflow-auto outline-none select-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset active:cursor-grabbing"
      >
        <div className="flex min-h-full w-max min-w-full p-6">
          <img
            src={props.image.src}
            alt={props.image.alt}
            draggable={false}
            onLoad={(event) => {
              const img = event.currentTarget;
              if (!given && img.naturalWidth > 0 && img.naturalHeight > 0) {
                setMeasured({ width: img.naturalWidth, height: img.naturalHeight });
              }
            }}
            className="m-auto block max-w-none"
            style={size ?? { width: "100%" }}
          />
        </div>
      </div>
    </>
  );
}
